import React from "react";
import InfoSquare, { NoteStrip } from "../../components/InfoSquare.jsx";
import {
  DISPLAY_TIMEZONE_LABEL,
  utcDayStartLocal,
  alignPreviousByWeekday,
  axisScale,
  money,
  money2,
  monthLong,
  basisPace,
  monthShort,
  pctText,
} from "./derive.js";

// A day is flagged when it runs this far above its weekday-matched day in the
// previous month. 40% is high enough to ignore ordinary drift and low enough
// to catch a resource that was left running.
const ANOMALY_THRESHOLD_PCT = 40;

function dayLabel(period, day) {
  const [year, month] = period.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/**
 * One bar per day of the month, with last month's weekday-matched level drawn
 * as a notch inside the bar.
 *
 * Not recharts, deliberately: this is a bar-per-day with an in-bar reference
 * mark and a mixed actual/forecast series, which is percentage heights on
 * divs and no library. recharts stays where it earns its weight, on Trend.
 */
export default function DailySpendChart({
  period,
  prevPeriod,
  compare,
  daily,
  basis,
  hover,
  onHover,
  openNote,
  onToggleNote,
}) {
  const days = daily?.days_in_month || 0;
  const series = (daily?.daily || []).map(Number);
  const elapsed = series.length;
  // The outlined bars use the same averaging window as the forecast stat
  // above them — a chart projecting one rate while the headline projects
  // another is how a dashboard loses trust.
  const forecastFill = elapsed && elapsed < days ? basisPace(series, basis) : 0;
  const aligned = alignPreviousByWeekday(period, daily?.previous_daily || []);

  const prevValues = compare ? aligned.map((a) => a?.value ?? 0) : [];
  const max = Math.max(0, ...series, forecastFill, ...prevValues);
  const { top, labels, gridlines } = axisScale(max);

  const isAnomaly = (i) => {
    if (!compare || i >= elapsed) return false;
    const prev = aligned[i]?.value;
    if (!prev || prev <= 0) return false;
    return series[i] > prev * (1 + ANOMALY_THRESHOLD_PCT / 100);
  };

  const anomalies = [];
  for (let i = 0; i < elapsed; i += 1) if (isAnomaly(i)) anomalies.push(i + 1);

  const prevLong = monthLong(prevPeriod);
  const caption = compare
    ? anomalies.length
      ? `${anomalies.length} ${anomalies.length === 1 ? "day" : "days"} ran more than ${ANOMALY_THRESHOLD_PCT}% above the matching weekday in ${prevLong}, starting ${dayLabel(period, anomalies[0])}.`
      : `No day ran more than ${ANOMALY_THRESHOLD_PCT}% above the matching weekday in ${prevLong}.`
    : `Comparison is off — ${prevLong} is not shown.`;

  const columns = Array.from({ length: days }, (_, i) => i);

  return (
    <section className="spend-section">
      <div className="section-head">
        <h2>
          Daily spend
          <InfoSquare id="daily" openNote={openNote} onToggle={onToggleNote} label="About these daily figures" />
        </h2>
        <div className="chart-legend">
          <span className="legend-item">
            <span className="legend-swatch" /> Actual
          </span>
          {compare && (
            <span className="legend-item">
              <span className="legend-swatch legend-swatch-anomaly" /> Over {monthShort(prevPeriod)} by {ANOMALY_THRESHOLD_PCT}%+
            </span>
          )}
          {forecastFill > 0 && (
            <span className="legend-item">
              <span className="legend-swatch legend-swatch-forecast" /> Forecast
            </span>
          )}
          {compare && (
            <span className="legend-item">
              <span className="legend-swatch legend-swatch-prev" /> {monthShort(prevPeriod)}, same weekday
            </span>
          )}
        </div>
      </div>

      <NoteStrip id="daily" openNote={openNote}>
        These are actual daily figures from Cost Explorer, not the month total divided by days elapsed. The most recent
        day is usually still settling, so it reads low until AWS finalises it. Each day is compared against the nearest
        day of {prevLong} with the same weekday, not the same date — comparing a Tuesday against a Saturday would
        manufacture an anomaly out of the ordinary weekly pattern. AWS bills by UTC day, so a bar here covers {utcDayStartLocal()} to
        {utcDayStartLocal()} {DISPLAY_TIMEZONE_LABEL}; clock times elsewhere on the page are also {DISPLAY_TIMEZONE_LABEL}.
      </NoteStrip>

      <div className="chart-wrap">
        <div className="chart-y" aria-hidden="true">
          {labels.map((t) => (
            <div key={t} className="chart-y-label" style={{ bottom: `${(t / top) * 100}%` }}>
              {money(t)}
            </div>
          ))}
        </div>

        <div className="chart-plot">
          {gridlines.map((t) => (
            <div key={t} className="chart-gridline" style={{ bottom: `${(t / top) * 100}%` }} aria-hidden="true" />
          ))}

          <div className="chart-bars">
            {columns.map((i) => {
              const day = i + 1;
              const actual = i < elapsed ? series[i] : null;
              const value = actual === null ? forecastFill : actual;
              const prev = compare ? aligned[i]?.value : null;
              const height = top ? Math.min(100, (value / top) * 100) : 0;
              const prevHeight = top && prev ? Math.min(100, (prev / top) * 100) : null;
              const inside = prevHeight !== null && prevHeight <= height;

              return (
                <button
                  key={day}
                  type="button"
                  className="chart-col"
                  onMouseEnter={() => onHover(i)}
                  onMouseLeave={() => onHover(null)}
                  onFocus={() => onHover(i)}
                  onBlur={() => onHover(null)}
                  aria-label={`${dayLabel(period, day)}: ${money2(value)}${actual === null ? " forecast" : ""}`}
                >
                  {(actual !== null || forecastFill > 0) && (
                    <span
                      className={`chart-bar${actual === null ? " chart-bar-forecast" : isAnomaly(i) ? " chart-bar-anomaly" : ""}`}
                      style={{ height: `${height}%` }}
                    />
                  )}
                  {prevHeight !== null && (
                    <span
                      className={`chart-prev-tick${inside ? " chart-prev-tick-inside" : ""}`}
                      style={{ bottom: `${prevHeight}%` }}
                    />
                  )}
                </button>
              );
            })}
          </div>

          {hover !== null && hover < days && (
            <Tooltip
              period={period}
              prevPeriod={prevPeriod}
              index={hover}
              days={days}
              value={hover < elapsed ? series[hover] : forecastFill}
              isForecast={hover >= elapsed}
              previous={compare ? aligned[hover] : null}
            />
          )}
        </div>

        <div className="chart-x" aria-hidden="true">
          {columns.map((i) => (
            <div key={i} className="chart-x-cell">
              {i === 0 || (i + 1) % 5 === 0 ? i + 1 : ""}
            </div>
          ))}
        </div>
      </div>

      <p className="chart-caption">{caption}</p>
    </section>
  );
}

function Tooltip({ period, prevPeriod, index, days, value, isForecast, previous }) {
  // Clamped away from the gutters so the first and last day's tooltip does not
  // hang off the page — the arrowless box has no anchor to give away the shift.
  const left = Math.min(96, Math.max(4, ((index + 0.5) / days) * 100));
  const prevValue = previous?.value;
  const pct = prevValue ? ((value - prevValue) / prevValue) * 100 : null;

  return (
    <div className="chart-tooltip" style={{ left: `${left}%` }} role="status">
      <div className="chart-tooltip-date">
        {dayLabel(period, index + 1)}
        {isForecast ? " · forecast" : ""}
      </div>
      <div className="chart-tooltip-amount">{money2(value)}</div>
      {previous && (
        <div className="chart-tooltip-compare">
          {prevValue === null || prevValue === undefined
            ? `${monthLong(prevPeriod)}: no data`
            : `${dayLabel(prevPeriod, previous.day)}: ${money2(prevValue)}${pct === null ? "" : `  (${pct > 0 ? "+" : "−"}${pctText(pct)})`}`}
        </div>
      )}
    </div>
  );
}
